<template>
  <section class="w-full max-w-md mx-auto">
    <UCard
      :ui="{
        body: { padding: 'p-0' },
        background: 'bg-white dark:bg-gray-900',
      }"
    >
      <div :class="heroBlockClasses">
        <div
          v-if="phase === 'BEFORE_RAMADAN' && !isCityDropdownOpen"
          class="px-4 sm:px-6 pt-2 sm:pt-3"
        >
          <div
            class="rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-800/35 px-3 py-2 text-xs sm:text-sm text-gray-700 dark:text-gray-300"
          >
            <p>{{ ramadanStartLine }}</p>
            <p class="mt-1">{{ firstRozaLine }}</p>
          </div>
        </div>
        <div v-if="!isCityDropdownOpen" class="px-4 sm:px-6 pt-2">
          <h1
            class="text-sm font-medium text-gray-700 dark:text-gray-300 leading-relaxed"
          >
            {{ heroTitle }}
          </h1>
          <p
            id="city-seo-dynamic"
            class="mt-1 text-sm leading-6 text-gray-500 dark:text-gray-400"
          >
            {{ heroDescription }}
          </p>
        </div>
        <!-- Countdown first (hero) -->
        <TimeCountdown
          v-if="showCountdown && !isCityDropdownOpen"
          :label="
            phase === 'BEFORE_RAMADAN' ? countdownLabel : ramadanEventLabel
          "
          :timeLeft="countdownMs ?? 0"
          :formatted="phase === 'BEFORE_RAMADAN' ? '' : countdown"
          :subtext="countdownSubtext"
        />

        <div
          v-if="showCountdown && !isCityDropdownOpen"
          class="px-4 sm:px-6 pb-2 text-center"
        >
          <NuxtLink
            to="/ramadan/rehnumai/"
            class="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 underline-offset-2 hover:underline"
          >
            Sehri o Iftar Rehnumai (Sehat & Sunnah Guide)
          </NuxtLink>
        </div>

        <!-- Short status text below timer -->
        <div
          v-if="
            today?.isRamadan && phase === 'DURING_RAMADAN' && rozaDayNumber > 0
          "
          class="px-4 sm:px-6 py-2 text-center"
        >
          <p class="text-base font-medium text-gray-800 dark:text-gray-200">
            {{ todayRozaHeadline }}
          </p>
          <p class="mt-0.5 text-sm text-gray-600 dark:text-gray-300">
            {{ ramadanProgressLine }}
          </p>
          <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
            {{ ramadanContextLine }}
          </p>
          <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
            {{ emotionalRozaLine }}
          </p>
        </div>

        <!-- Header with City Dropdown -->
        <TimeHeader
          :cities="availableCities"
          :selectedCityKey="selectedCityKey"
          :ramadanLabel="monthBadgeLabel"
          @citySelected="selectCity"
          @cityOpenChange="isCityDropdownOpen = $event"
        />

        <DateContext :gregorian="gregorianDate" :hijri="hijriDate" />
        <div v-if="routeCityMeta && today?.isRamadan" class="px-4 sm:px-6 pb-2">
          <div class="flex items-center justify-between gap-3">
            <p class="text-sm font-medium text-gray-800 dark:text-gray-200">
              {{ cityTimingShareTitle }}
            </p>
            <a
              :href="cityTimingShareUrl"
              target="_blank"
              rel="noopener noreferrer"
              class="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md px-2 py-1 text-xs font-medium text-gray-700 hover:text-gray-900 dark:text-gray-200 dark:hover:text-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-600 dark:focus-visible:ring-gray-300"
              :aria-label="`Share today's ${routeCityMeta.name} roza timing on WhatsApp`"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor"
                class="h-3.5 w-3.5"
                aria-hidden="true"
              >
                <path
                  d="M12.04 2C6.55 2 2.1 6.44 2.1 11.93c0 1.76.46 3.48 1.34 5L2 22l5.22-1.37a9.9 9.9 0 0 0 4.82 1.23h.01c5.48 0 9.94-4.44 9.94-9.93S17.53 2 12.04 2Zm5.79 14.1c-.24.67-1.36 1.25-1.89 1.33-.49.07-1.12.1-1.8-.11-.41-.13-.94-.31-1.62-.61-2.85-1.23-4.7-4.1-4.84-4.29-.14-.19-1.16-1.54-1.16-2.94s.73-2.09.99-2.38c.26-.28.57-.35.76-.35h.55c.17 0 .4-.06.62.47.24.58.83 2 .9 2.14.07.14.12.31.02.5-.1.19-.16.31-.31.48-.16.17-.33.38-.47.51-.16.16-.32.33-.14.64.18.31.8 1.32 1.72 2.14 1.18 1.05 2.17 1.38 2.48 1.53.31.16.49.14.67-.08.19-.23.79-.92 1-1.24.21-.31.43-.26.72-.16.3.1 1.86.88 2.18 1.03.31.16.52.24.59.38.07.14.07.81-.17 1.48Z"
                />
              </svg>
              <span>Share</span>
            </a>
          </div>
        </div>

        <div v-if="!isCityDropdownOpen" class="pb-2">
          <!-- Reference Times -->
          <TimeReference
            v-if="phase === 'DURING_RAMADAN'"
            :items="referenceItems"
          />

          <!-- Roza Duration -->
          <div
            v-if="today?.isRamadan && rozaDuration"
            class="mt-4 sm:mt-6 px-4 sm:px-6 py-3 sm:py-3.5 text-center border-t border-b border-gray-100 dark:border-gray-800"
          >
            <div
              class="flex items-center justify-center gap-2 text-gray-600 dark:text-gray-400"
            >
              <UIcon name="i-heroicons-clock" class="w-4 h-4 sm:w-5 sm:h-5" />
              <span class="text-sm sm:text-base font-medium text-gray-900 dark:text-gray-100">
                Aaj ka roza {{ rozaDuration.hours }} ghante
                {{ rozaDuration.minutes }} minute ka
                {{ hasIftarStarted ? "tha." : "hai." }}
              </span>
            </div>
          </div>

          <!-- Disclaimer -->
          <UAlert
            v-if="isRamadan"
            color="amber"
            variant="soft"
            :ui="{
              padding: 'p-3',
              rounded: 'rounded-none',
              title: { base: 'text-xs' },
            }"
            class="mt-4"
          >
            <template #title>
              <span
                class="block whitespace-nowrap overflow-hidden text-ellipsis text-sm text-gray-500 dark:text-gray-400"
              >
                Sehr o Iftar timings mein 1 minute ka farq ho sakta hai.
              </span>
            </template>
          </UAlert>
        </div>
      </div>

      <div
        v-if="routeCityMeta"
        class="px-4 sm:px-6 py-3 border-t border-gray-200 dark:border-gray-700 text-sm leading-6 text-gray-500 dark:text-gray-400 whitespace-pre-line"
      >
        {{ citySectionText }}
      </div>

      <!-- Quick FAQ Section for Keyword Diversity -->
      <div
        class="px-4 sm:px-6 py-4 border-t border-gray-200 dark:border-gray-700 space-y-4"
      >
        <h2 class="text-base font-semibold text-gray-900 dark:text-gray-100">
          Ramadan Timings Pakistan 2026 Calendar - Quick Info
        </h2>

        <div class="space-y-3 text-sm">
          <!-- Q1 -->
          <details
            class="bg-gray-50 dark:bg-gray-800 p-3 rounded cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <summary class="font-medium text-gray-900 dark:text-gray-100">
              Aaj kitna roza hai?
            </summary>
            <p class="mt-2 text-gray-600 dark:text-gray-400">
              Aaj Pakistan mein Ramzan ka
              {{ formatRozaOrdinal(displayRozaDayNumber) }} roza hai. Ramzan
              {{ currentRamadanYear }} mein total 29 ya 30 rozay hote hain. Yeh
              roza number har din automatically update hota hai.
            </p>
          </details>

          <!-- Q2 -->
          <details
            class="bg-gray-50 dark:bg-gray-800 p-3 rounded cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <summary class="font-medium text-gray-900 dark:text-gray-100">
              Today Sehri Time Pakistan - What time to start fasting?
            </summary>
            <p class="mt-2 text-gray-600 dark:text-gray-400">
              Today ki Sehri time {{ cityData.cityName }} mein alag hai har
              shehar ke mutabiq.
              <NuxtLink
                to="/calendar/"
                class="text-emerald-700 dark:text-emerald-400 underline"
              >
                Ramadan calendar 2026 Pakistan
              </NuxtLink>
              mein aapke shehar ke liye Sehri aur Iftar exact time dekhein.
            </p>
            <p class="mt-2 text-gray-600 dark:text-gray-400">
              Sehri ke liye routine banana mushkil hota hai?
              <NuxtLink
                to="/blog/sehri-uthne-ka-tareeqa-ramzan-2026/"
                class="text-gray-700 dark:text-gray-300 underline underline-offset-2"
              >
                How to Wake Up for Sehri Every Day
              </NuxtLink>
            </p>
          </details>

          <!-- Q3 -->
          <details
            class="bg-gray-50 dark:bg-gray-800 p-3 rounded cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <summary class="font-medium text-gray-900 dark:text-gray-100">
              Iftar Time Lahore, Karachi, Quetta 2026 - When to break fast?
            </summary>
            <p class="mt-2 text-gray-600 dark:text-gray-400">
              Iftar time Lahore, Karachi, Quetta aur doosre sheher mein alag
              hote hain. Apne shehar select karein uppar se taake aapko exactly
              timing pata chale. Rozay app par live countdown bhi available hai.
            </p>
          </details>

          <!-- Q4 -->
          <details
            class="bg-gray-50 dark:bg-gray-800 p-3 rounded cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <summary class="font-medium text-gray-900 dark:text-gray-100">
              Ramadan 2026 - Ramadan kb shuru hoga Pakistan?
            </summary>
            <p class="mt-2 text-gray-600 dark:text-gray-400">
              Ramadan 2026 Pakistan mein 19 February 2026 se shuru hone ki
              mumkina hai, lekin yeh Ruyat-e-Hilal Committee ke chand dekhne ke
              baad confirm hota hai.
              <NuxtLink
                to="/ramadan-start/"
                class="text-emerald-700 dark:text-emerald-400 underline"
              >
                Ramadan kb shuru hoga - Official announcement dekhein
              </NuxtLink>
            </p>
          </details>

          <!-- Q5 -->
          <details
            class="bg-gray-50 dark:bg-gray-800 p-3 rounded cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <summary class="font-medium text-gray-900 dark:text-gray-100">
              Roza Timings Pakistan 2026 - How accurate are these timings?
            </summary>
            <p class="mt-2 text-gray-600 dark:text-gray-400">
              Hum Roza timings Pakistan ke liye sabse accurate data use karte
              hain. Roza timing har din update hote hain Ramadan calendar 2026
              Pakistan mein. Lekin 1 minute ka farq ho sakta hai local
              conditions ke mutabiq.
            </p>
          </details>
        </div>
      </div>

      <div
        v-if="routeCityMeta"
        class="px-4 sm:px-6 py-3 border-t border-gray-200 dark:border-gray-700 text-sm leading-6 text-gray-500 dark:text-gray-400"
      >
        <span class="mr-1">Useful links:</span>
        <NuxtLink
          to="/calendar/"
          class="hover:underline underline-offset-2 text-gray-600 dark:text-gray-300"
        >
          {{ routeCityMeta.name }} Ramadan calendar
        </NuxtLink>
        <span class="mx-1">|</span>
        <NuxtLink
          to="/dua/"
          class="hover:underline underline-offset-2 text-gray-600 dark:text-gray-300"
        >
          Ramadan ki duain
        </NuxtLink>
        <span class="mx-1">|</span>
        <NuxtLink
          to="/roza/"
          class="hover:underline underline-offset-2 text-gray-600 dark:text-gray-300"
        >
          Roza guide
        </NuxtLink>
        <span class="mx-1">|</span>
        <NuxtLink
          to="/ramadan-start/"
          class="hover:underline underline-offset-2 text-gray-600 dark:text-gray-300"
        >
          Ramadan start update
        </NuxtLink>
      </div>
    </UCard>

    <section
      id="ramadan-calendar-2026"
      class="mt-6 rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 px-4 sm:px-6 py-5"
      aria-labelledby="ramadan-calendar-2026-heading"
    >
      <h2
        id="ramadan-calendar-2026-heading"
        class="text-lg sm:text-xl font-semibold text-gray-900 dark:text-gray-100"
      >
        Ramadan 2026 Calendar – {{ selectedCityName }}, Pakistan
      </h2>
      <p class="mt-2 text-sm sm:text-base leading-6 text-gray-600 dark:text-gray-300">
        Pakistan ke shehar {{ selectedCityName }} ke liye Ramadan 2026 ka poora
        calendar neeche diya gaya hai. Is timetable mein har roz ka sehri aur
        iftar time mojood hai. Aap tamam 29/30 rozon ka schedule yahan check
        kar sakte hain.
      </p>

      <div
        class="mt-4 transition-[max-height] duration-300 ease-in-out"
        :class="[
          'overflow-hidden sm:overflow-visible',
          isCalendarExpanded
            ? 'max-h-[4000px] sm:max-h-none'
            : 'max-h-64 sm:max-h-none',
        ]"
      >
        <div class="overflow-x-auto">
          <table class="min-w-[640px] w-full text-sm text-left border-collapse">
          <caption class="mb-2 text-xs sm:text-sm text-gray-500 dark:text-gray-400">
            Ramadan 2026 Sehri &amp; Iftar Timetable – {{ selectedCityName }},
            Pakistan
          </caption>
          <thead>
            <tr class="border-b border-gray-200 dark:border-gray-700">
              <th scope="col" class="py-2 pr-3 font-semibold text-gray-800 dark:text-gray-100">
                Roza
              </th>
              <th scope="col" class="py-2 pr-3 font-semibold text-gray-800 dark:text-gray-100">
                Date (Gregorian)
              </th>
              <th scope="col" class="py-2 pr-3 font-semibold text-gray-800 dark:text-gray-100">
                Sehri
              </th>
              <th scope="col" class="py-2 font-semibold text-gray-800 dark:text-gray-100">
                Iftar
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="day in ramadanCalendarDays"
              :key="day.gregorianDate"
              class="border-b border-gray-100 dark:border-gray-800 last:border-0"
            >
              <td class="py-2 pr-3 text-gray-700 dark:text-gray-200">
                {{ day.rozaNumber }}
              </td>
              <td class="py-2 pr-3 text-gray-700 dark:text-gray-200">
                {{ day.gregorianDate }}
              </td>
              <td class="py-2 pr-3 text-gray-700 dark:text-gray-200">
                {{ formatTime(day.sehriEnds) }}
              </td>
              <td class="py-2 text-gray-700 dark:text-gray-200">
                {{ formatTime(day.iftarTime) }}
              </td>
            </tr>
          </tbody>
        </table>
        </div>
      </div>

      <div class="mt-3 sm:hidden">
        <button
          type="button"
          class="w-full rounded-lg border border-gray-200 dark:border-gray-800 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800/60 transition-colors"
          @click="isCalendarExpanded = !isCalendarExpanded"
          :aria-expanded="isCalendarExpanded"
          aria-controls="ramadan-calendar-2026"
        >
          {{ isCalendarExpanded ? "Hide Full Calendar" : "View Full Calendar" }}
        </button>
      </div>

      <div class="mt-4 border-t border-gray-200 dark:border-gray-800 pt-4">
        <h3 class="text-sm font-semibold text-gray-900 dark:text-gray-100">
          Ramadan Important Dates 2026
        </h3>
        <ul class="mt-2 flex flex-wrap gap-3 text-sm">
          <li>
            <NuxtLink
              to="/shab-e-qadr/"
              class="text-emerald-700 dark:text-emerald-400 underline underline-offset-2"
            >
              Shab-e-Qadr 2026 schedule
            </NuxtLink>
          </li>
          <li>
            <NuxtLink
              to="/itikaf/"
              class="text-emerald-700 dark:text-emerald-400 underline underline-offset-2"
            >
              Itikaf dates in Ramadan 2026
            </NuxtLink>
          </li>
          <li>
            <NuxtLink
              to="/jumma-tul-wida/"
              class="text-emerald-700 dark:text-emerald-400 underline underline-offset-2"
            >
              Jumma-tul-Wida 2026 info
            </NuxtLink>
          </li>
          <li>
            <NuxtLink
              to="/karachi/"
              class="text-gray-700 dark:text-gray-200 underline underline-offset-2"
            >
              Karachi Sehri &amp; Iftar times
            </NuxtLink>
          </li>
          <li>
            <NuxtLink
              to="/lahore/"
              class="text-gray-700 dark:text-gray-200 underline underline-offset-2"
            >
              Lahore Ramadan timetable
            </NuxtLink>
          </li>
        </ul>
      </div>
    </section>

    <!-- Notification Signup -->
    <div class="mt-3">
      <!-- <NotificationSignup /> -->
    </div>

    <InternalLinks
      current="time"
      :show-city-links="true"
      :selected-city-key="selectedCityKey"
      class="mt-3"
    />

    <UNotifications />
  </section>
</template>

<script setup lang="ts">
import InternalLinks from "@/components/seo/InternalLinks.vue";
import { useRamadanTime } from "@/composables/useRamadanTime";
import { formatRomanHijriDate } from "@/composables/useRomanHijriDate";
import {
  buildCityShareText,
  buildWhatsAppUrl,
  type CityShareState,
} from "@/composables/useWhatsAppShare";
import { seoConfig } from "@/config/seo.config";
import { cities, cityKeys } from "@/data/cities";
import type { CityKey } from "@/domain/ramadan";
import { computed, onMounted, ref, watch } from "vue";

const route = useRoute();
const router = useRouter();

const toast = useToast();
const isCityDropdownOpen = ref(false);
const isCalendarExpanded = ref(false);

const {
  now,
  cityData,
  today,
  normalizedDays,
  currentEvent,
  phase,
  countdown,
  countdownMs,
  showCountdown,
  daysUntilRamadan,
  remainingRoza,
  setCity,
} = useRamadanTime();

/* -----------------------------
   City selection
----------------------------- */
type CityOption = { key: CityKey; name: string };

const availableCities = cityKeys.map((key) => ({
  key,
  name: cities[key].name,
})) satisfies CityOption[];

const STORAGE_KEY = "rozay_selected_city";

const selectedCityKeyLocal = ref<CityKey>("islamabad");

const selectedCityKey = computed<CityKey>(() => selectedCityKeyLocal.value);
const selectedCityName = computed(() => {
  const match = availableCities.find((c) => c.key === selectedCityKey.value);
  return match?.name || cityData.value.cityName || "Pakistan";
});

const normalizedRoutePath = computed(
  () => route.path.replace(/\/+$/, "") || "/",
);

const routeCityKey = computed<CityKey | null>(() => {
  const slug = normalizedRoutePath.value.replace(/^\/+/, "");
  if (!slug) return null;
  const match = availableCities.find((c) => c.key === slug);
  return match?.key ?? null;
});

const routeCityMeta = computed(() => {
  if (!routeCityKey.value) return null;
  return cities[routeCityKey.value];
});

const selectCity = (city: CityOption) => {
  selectedCityKeyLocal.value = city.key;
  setCity(city.key);

  // Persist to localStorage
  if (process.client) {
    localStorage.setItem(STORAGE_KEY, city.key);
  }

  // Update URL only when user is on root or already on a city-specific path.
  // This prevents unwanted route changes when other sections (e.g. /roza)
  // are active and the city selector is used.
  const currentPath = normalizedRoutePath.value;
  const isOnRoot = currentPath === "/" || currentPath === "";
  const isOnCityPath = availableCities.some((c) => currentPath === `/${c.key}`);
  if (isOnRoot || isOnCityPath) {
    // replace rather than push to avoid polluting history
    router.replace(`/${city.key}/`).catch(() => {});
  }

  toast.add({
    title: "Shehar update",
    description: `Ab timings ${city.name} ke mutabiq hain`,
    icon: "i-heroicons-map-pin",
    timeout: 1800,
  });
};

function applyCity(cityKey: CityKey) {
  if (selectedCityKeyLocal.value === cityKey) return;
  selectedCityKeyLocal.value = cityKey;
  setCity(cityKey);
  if (process.client) {
    localStorage.setItem(STORAGE_KEY, cityKey);
  }
}

onMounted(() => {
  // Route slug has highest priority (city intent)
  if (routeCityKey.value) {
    applyCity(routeCityKey.value);
    return;
  }

  // Load from localStorage next
  let initialCity: CityKey | null = null;
  if (process.client) {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && availableCities.some((c) => c.key === stored)) {
      initialCity = stored as CityKey;
    }
  }

  // Fall back to current city data
  if (!initialCity) {
    const found = availableCities.find((c) => c.name === cityData.cityName);
    initialCity = found?.key ?? "islamabad";
  }

  applyCity(initialCity);

  watch(
    () => cityData.cityName,
    (newCityName) => {
      const f = availableCities.find((c) => c.name === newCityName);
      if (f) selectedCityKeyLocal.value = f.key;
    },
  );
});

watch(routeCityKey, (key) => {
  if (key) applyCity(key);
});

const seo = computed(() => {
  const cityKey = routeCityKey.value;
  if (!cityKey) return seoConfig.time;
  const city = cities[cityKey];
  return {
    title: city.seoTitle,
    description: city.seoDescription,
    canonical: `https://rozay.today/${cityKey}/`,
  };
});

const {
  generateFAQSchema,
  generateWebPageSchema,
  generateEventSchema,
  generateBreadcrumbSchema,
  addSchema,
} = useSchemaMarkup();

const webPageSchema = computed(() =>
  generateWebPageSchema({
    name: seo.value.title,
    description: seo.value.description,
    url: seo.value.canonical,
  }),
);

const faqSchema = computed(() => {
  const cityName = routeCityMeta.value?.name || "Pakistan";
  const cityFaq = routeCityMeta.value?.faq;
  return generateFAQSchema([
    {
      question: "Aaj kitna roza hai?",
      answer: rozaCountFaqAnswer.value,
    },
    {
      question:
        cityFaq?.question ||
        `Today Sehri Time ${cityName} - What time to start fasting?`,
      answer:
        cityFaq?.answer ||
        "Sehri and Iftar times vary by city. Select your city to view exact daily timings.",
    },
    {
      question: `Iftar Time ${cityName} 2026 - When to break fast?`,
      answer:
        "Break the fast at Maghrib based on your selected city's daily Ramadan timing.",
    },
    {
      question: "Ramadan 2026 - When will Ramadan start in Pakistan?",
      answer:
        "Ramadan start depends on official moon sighting announcements in Pakistan.",
    },
    {
      question: "How accurate are these Ramadan timings?",
      answer:
        "Timings are provided daily per city, but local conditions can cause small differences.",
    },
  ]);
});

const eventSchema = computed(() =>
  generateEventSchema({
    name: `Ramadan 2026 Timings - ${routeCityMeta.value?.name || "Pakistan"}`,
    description: seo.value.description,
    startDate: "2026-02-19",
    endDate: "2026-03-20",
    location: routeCityMeta.value?.name || "Pakistan",
    url: seo.value.canonical,
  }),
);

const breadcrumbSchema = computed(() => {
  const items = [{ name: "Home", url: "https://rozay.today/" }];
  if (routeCityMeta.value) {
    items.push({
      name: `${routeCityMeta.value.name} Sehri & Iftar Time`,
      url: seo.value.canonical,
    });
  }
  return generateBreadcrumbSchema(items);
});

// Setup schemas in head
onMounted(() => {
  addSchema(webPageSchema.value);
  addSchema(faqSchema.value);
  addSchema(eventSchema.value);
  addSchema(breadcrumbSchema.value);
});

useHead(() => {
  const cityName = routeCityMeta.value?.name || "Pakistan";

  return {
    title: seo.value.title,
    meta: [
      { name: "description", content: seo.value.description },
      {
        name: "keywords",
        content: `Ramadan timings ${cityName} 2026 calendar, ${cityName} Sehri and Iftar time today, Roza time ${cityName} 2026, Roza timings Pakistan, Ramzan timetable Pakistan 2026, Ramazan calendar 2026, Ramdan timings, Sehri time today, Sehr time today, Sehr end time, Sahur time ${cityName}, Suhoor time ${cityName}, Iftar time today, Iftari time Pakistan, Aftari time ${cityName}, Ramadan 2026 start date Pakistan, Ramzan moon sighting Pakistan 2026, Ramadan calendar 2026 Pakistan, Sehri Iftar schedule, Rozay timing, sehri iftar time lahore 2026, iftar time karachi today, sehri time lahore today, islamabad ramzan timetable 2026, prayer timings, Hijri 1447 AH`,
      },
      // OpenGraph Tags
      { property: "og:title", content: seo.value.title },
      { property: "og:description", content: seo.value.description },
      { property: "og:type", content: "website" },
      { property: "og:url", content: seo.value.canonical },
      {
        property: "og:site_name",
        content: "Rozay - Ramadan 2026 Pakistan Guide",
      },
      // Twitter Card Tags
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: seo.value.title },
      { name: "twitter:description", content: seo.value.description },
    ],
    link: [{ rel: "canonical", href: seo.value.canonical }],
  };
});

/* -----------------------------
   Helpers
----------------------------- */
const isRamadan = computed(() => !!today.value?.isRamadan);

const formatTime = (time24: string) => {
  const [hh, mm] = time24.split(":");
  const h = Number(hh);
  if (!Number.isFinite(h) || !mm) return time24;
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 || 12;
  return `${h12}:${mm} ${ampm}`;
};

const RAMADAN_START_DATE_SHORT = "19 Feb";
const RAMADAN_START_DATE_FULL = "19 Feb 2026";
const TOTAL_RAMADAN_DAYS = 30;

const rozaDayNumber = computed(() => {
  const n = Number(today.value?.rozaNumber ?? 0);
  return Number.isFinite(n) && n > 0 ? n : 0;
});

const displayRozaDayNumber = computed(() => rozaDayNumber.value || 1);

const formatRozaOrdinal = (value: number): string => {
  const mod10 = value % 10;
  const mod100 = value % 100;
  if (mod10 === 1 && mod100 !== 11) return `${value}st`;
  if (mod10 === 2 && mod100 !== 12) return `${value}nd`;
  if (mod10 === 3 && mod100 !== 13) return `${value}rd`;
  return `${value}th`;
};

const formatRozaUrduOrdinal = (value: number): string => `${value}wa`;

const todayRozaHeadline = computed(() => {
  if (!rozaDayNumber.value) return "";
  return "Aaj kitna roza hai?";
});

const ramadanProgressLine = computed(() => {
  if (!rozaDayNumber.value) return "";
  return `Aaj Ramzan ${currentRamadanYear.value} ka ${formatRozaUrduOrdinal(rozaDayNumber.value)} roza hai \u{1F319}`;
});

const ramadanContextLine = computed(() => {
  if (!rozaDayNumber.value) return "";
  const hijriLabel =
    today.value?.hijriDate || `${rozaDayNumber.value} Ramadan 1447 AH`;
  return `(${hijriLabel})`;
});

const rozaCountFaqAnswer = computed(() => {
  return `Aaj Pakistan mein Ramzan ka ${formatRozaOrdinal(displayRozaDayNumber.value)} roza hai. Ramzan ${currentRamadanYear.value} mein total 29 ya 30 rozay hote hain. Yeh roza number har din automatically update hota hai.`;
});

const emotionalRozaLine = computed(() => {
  if (!rozaDayNumber.value) return "";
  return `Allah qabool kare \u{1F932}`;
});

const ramadanStartLine = computed(() => {
  if (phase.value === "BEFORE_RAMADAN") return "Ramadan 2026 Pakistan";
  return `Ramadan 2026 Pakistan: ${RAMADAN_START_DATE_SHORT} se shuru hua`;
});

const firstRozaLine = computed(() => {
  if (phase.value === "BEFORE_RAMADAN")
    return `${RAMADAN_START_DATE_FULL} se shuru`;
  return `Pehla roza: ${RAMADAN_START_DATE_FULL}`;
});

/* -----------------------------
   Countdown label
----------------------------- */
const countdownLabel = computed(() => {
  // Before Ramadan countdown
  if (phase.value === "BEFORE_RAMADAN") {
    const displayDays = Math.max(0, daysUntilRamadan.value - 1);

    if (daysUntilRamadan.value === 1) return "Kal pehla roza hoga (InshaAllah)";

    if (displayDays > 1) {
      return `Ramzan shuru honay mein ${displayDays} din baqi hain`;
    }
    if (displayDays === 1) {
      return "Kal pehla roza hoga (InshaAllah)";
    }
    return "";
  }

  // During Ramadan - show remaining roza count
  if (phase.value === "DURING_RAMADAN") {
    if (today.value && today.value.isRamadan) {
      if (remainingRoza.value === 0) {
        return "Ramadan ka aakhri roza";
      }
      return `${remainingRoza.value} roza baqi hain`;
    }
    return "";
  }

  // After Ramadan
  if (phase.value === "AFTER_RAMADAN") return "";

  return "";
});

/* Ramadan event label (for timer display) */
const ramadanEventLabel = computed(() => {
  if (phase.value !== "DURING_RAMADAN") return "";

  if (currentEvent.value === "SEHRI") return "Sehri khatam honay mein";
  if (currentEvent.value === "IFTAR") return "Iftaar tak waqt baqi hai";

  return "";
});

const countdownSubtext = computed(() => {
  if (phase.value !== "DURING_RAMADAN") return "";
  const ms = countdownMs.value ?? 0;
  if (ms <= 0) return "";

  const totalMinutes = Math.floor(ms / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (currentEvent.value === "IFTAR") {
    if (hours <= 0) return `Iftaar tak ${minutes} minute baqi hain.`;
    return `Iftaar tak ${hours} ghante ${minutes} minute baqi hain.`;
  }

  if (currentEvent.value === "SEHRI") {
    if (hours <= 0) return `Sehri tak ${minutes} minute baqi hain.`;
    return `Sehri tak ${hours} ghante ${minutes} minute baqi hain.`;
  }

  return "";
});

/* -----------------------------
   Reference items
----------------------------- */
const todayIndex = computed(() => {
  const g = today.value?.gregorianDate;
  if (!g) return -1;
  return normalizedDays.value.findIndex((d) => d.gregorianDate === g);
});

const referenceItems = computed(() => {
  if (!today.value || !today.value.isRamadan) return [];
  const idx = todayIndex.value;
  if (idx === -1) return [];

  const tomorrow = normalizedDays.value[idx + 1];

  // Build timestamps to check if we're still in morning or past iftar
  const sehriEndsToday = new Date(
    `${today.value.gregorianDate}T${today.value.sehriEnds}:00+05:00`,
  );
  const iftarToday = new Date(
    `${today.value.gregorianDate}T${today.value.iftarTime}:00+05:00`,
  );
  const currentTime = new Date(now.value);

  // During sehri time (morning - before sehri ends)
  if (currentTime < sehriEndsToday) {
    return [
      {
        label: "Sehri khatam hone ka waqt",
        time: formatTime(today.value.sehriEnds),
      },
      { label: "Aaj ki iftaar", time: formatTime(today.value.iftarTime) },
    ];
  }

  // During iftar time (after sehri, before/at iftar)
  if (currentTime < iftarToday || currentEvent.value === "IFTAR") {
    return [
      { label: "Aaj ki iftaar", time: formatTime(today.value.iftarTime) },
      {
        label: "Sehri khatam ho chuki hai",
        time: formatTime(today.value.sehriEnds),
        muted: true,
        done: true,
      },
    ];
  }

  // After iftar - show tomorrow's sehri
  if (tomorrow?.isRamadan) {
    return [
      { label: "Kal ki sehri", time: formatTime(tomorrow.sehriEnds) },
      {
        label: "Aaj ki iftaar",
        time: formatTime(today.value.iftarTime),
        muted: true,
        done: true,
      },
    ];
  }

  return [];
});

/* -----------------------------
   Roza duration
----------------------------- */
const rozaDuration = computed(() => {
  if (!today.value?.isRamadan) return null;

  const sehriEnds = today.value.sehriEnds; // "04:30"
  const iftar = today.value.iftarTime; // "17:11"

  const [sehriH, sehriM] = sehriEnds.split(":").map(Number);
  const [iftarH, iftarM] = iftar.split(":").map(Number);

  let hours = iftarH - sehriH;
  let minutes = iftarM - sehriM;

  // Handle minute borrow
  if (minutes < 0) {
    hours--;
    minutes += 60;
  }

  return { hours, minutes };
});

const hasIftarStarted = computed(() => {
  if (!today.value?.isRamadan) return false;
  const iftarToday = new Date(
    `${today.value.gregorianDate}T${today.value.iftarTime}:00+05:00`,
  );
  return now.value >= iftarToday;
});

const todaySehri = computed(() => {
  if (!today.value?.sehriEnds) return "--";
  return formatTime(today.value.sehriEnds);
});

const todayIftar = computed(() => {
  if (!today.value?.iftarTime) return "--";
  return formatTime(today.value.iftarTime);
});

const currentRamadanYear = computed(() => {
  const firstRamadanDay = normalizedDays.value.find((d) => d.isRamadan);
  const yearFromSchedule = firstRamadanDay?.gregorianDate?.match(/^\d{4}/)?.[0];
  if (yearFromSchedule) return yearFromSchedule;

  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    timeZone: "Asia/Karachi",
  }).format(now.value);
});

const currentIslamicMonthLabel = computed(() => {
  const label = today.value?.hijriDate || hijriDate.value;
  if (/\bShawwal\b/i.test(label)) return "Shawwal";
  return "Ramadan";
});

const monthBadgeLabel = computed(
  () => `${currentIslamicMonthLabel.value} ${currentRamadanYear.value}`,
);

const citySectionText = computed(
  () => routeCityMeta.value?.citySection.trim() || "",
);

const heroTitle = computed(() => {
  if (!routeCityMeta.value) return `Pakistan Roza Countdown ${currentRamadanYear.value}`;
  if (isRamadan.value) {
    return `${cityData.value.cityName} Sehri & Iftar Time Today (Ramadan ${currentRamadanYear.value})`;
  }
  return `${cityData.value.cityName} ${currentIslamicMonthLabel.value} ${currentRamadanYear.value} Timings - Sehri & Iftar`;
});

const heroDescription = computed(() => {
  if (!routeCityMeta.value) {
    return `Pakistan mein aaj ka Sehri time ${todaySehri.value} aur Iftar time ${todayIftar.value} city ke mutabiq vary karta hai. Ramadan ${currentRamadanYear.value} ke dauran roza countdown aur daily updates yahan milte hain.`;
  }
  if (currentIslamicMonthLabel.value === "Shawwal") {
    return `${cityData.value.cityName} mein aaj ka Sehri time ${todaySehri.value} aur Iftar time ${todayIftar.value} hai. Shawwal ${currentRamadanYear.value} ke mutabiq Hijri date aur city timings yahan update hoti hain.`;
  }
  return `${cityData.value.cityName} mein aaj ka Sehri time ${todaySehri.value} aur Iftar time ${todayIftar.value} hai. Ramadan ${currentRamadanYear.value} ke dauran roza timings yahan daily update hoti hain.`;
});

const cityTimingShareTitle = computed(() => {
  if (!routeCityMeta.value) return "";
  const rozaNumber = today.value?.rozaNumber;
  return rozaNumber
    ? `Aaj ka Roza - ${routeCityMeta.value.name} (Roza ${rozaNumber})`
    : `Aaj ka Roza - ${routeCityMeta.value.name}`;
});

const cityTimingShareState = computed<CityShareState>(() => {
  if (currentEvent.value === "NONE") return "after_iftar";
  if (currentEvent.value === "IFTAR") return "before_iftar";
  return "before_sehri";
});

const cityTimingShareNextSehri = computed(() => {
  const idx = todayIndex.value;
  if (idx === -1) return "";
  const tomorrow = normalizedDays.value[idx + 1];
  if (!tomorrow?.isRamadan) return "";
  return formatTime(tomorrow.sehriEnds);
});

const cityTimingShareDateLabel = computed(() => {
  const parts = [gregorianDate.value, hijriDate.value].filter(Boolean);
  return parts.join(" | ");
});

const cityTimingShareUrl = computed(() => {
  if (!routeCityMeta.value || !today.value) return "https://wa.me/";
  const message = buildCityShareText({
    city: routeCityMeta.value.name,
    sehri: formatTime(today.value.sehriEnds),
    iftar: formatTime(today.value.iftarTime),
    dateLabel: cityTimingShareDateLabel.value,
    url: seo.value.canonical,
    state: cityTimingShareState.value,
    nextSehri: cityTimingShareNextSehri.value,
  });
  return buildWhatsAppUrl(message);
});

const heroBlockClasses = computed(() => {
  // Keep hero compact to avoid forced extra scroll on small screens.
  return "flex flex-col";
});

const ramadanCalendarDays = computed(() =>
  normalizedDays.value.filter((day) => day.isRamadan),
);

/* -----------------------------
   Date context
----------------------------- */
const gregorianDate = computed(() => {
  const d = new Date(now.value);
  return new Intl.DateTimeFormat("en-US", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "Asia/Karachi",
  }).format(d);
});

const hijriDate = computed(() => {
  // Prefer dataset Hijri during Ramadan days for consistency with schedule data.
  if (today.value?.hijriDate) return today.value.hijriDate;

  const formatted = formatRomanHijriDate(new Date(now.value), "Asia/Karachi");

  // Keep pre-Ramadan display aligned with local Pakistan moon-sighting context.
  // Some runtime Islamic calendars can roll to 1 Ramadan earlier.
  if (phase.value === "BEFORE_RAMADAN" && /Ramadan/i.test(formatted)) {
    const yearMatch = formatted.match(/(\d{4})\s*AH/i);
    const hijriYear = yearMatch?.[1] ?? "";
    return `29 Shaban${hijriYear ? ` ${hijriYear}` : ""} AH`.trim();
  }

  return formatted;
});

/* -----------------------------
   Iftar toast (client-only)
----------------------------- */
onMounted(() => {
  const lastIftarToastKey = ref<string | null>(null);

  watch(currentEvent, (n, o) => {
    const day = today.value?.gregorianDate;
    const city = cityData.value?.cityKey || cityData.value?.cityName;
    if (!day || !city) return;

    // Fire only when countdown actually reaches iftar (IFTAR -> NONE),
    // not when the city changes during the daytime IFTAR phase.
    if (o !== "IFTAR" || n !== "NONE") return;

    const toastKey = `${city}:${day}`;
    if (lastIftarToastKey.value === toastKey) return;
    lastIftarToastKey.value = toastKey;

    toast.add({
      title: "Iftar Time!",
      description: "Time to break your fast",
      icon: "i-heroicons-bell",
      timeout: 10000,
    });
  });
});
</script>






